import { useTranslation } from 'react-i18next';
import { Archive, ArrowLeft, Download, FileArchive, Image as ImageIcon, Info, Lock, LockOpen, Moon, Scale, Sun } from 'lucide-react';

import LocalePicker from './LocalePicker';
import SaveStateIndicator from './canvas/SaveStateIndicator';
import {
    BrandLogo,
    Button,
    IconButton,
    MenuDropdown,
    type MenuSection,
} from './ui';
import { cn } from '../lib/cn';
import type { Theme } from '../types/api';

/**
 * AnnotatorHeader — top navbar of the Annotator view. Extracted out of
 * Annotator.tsx per "keep files small" rule.
 */
interface AnnotatorHeaderProps {
    isDarkMode: boolean;
    isIgnodeMode: boolean;
    projectName: string | undefined;
    imageCount: number;
    classCount: number;
    hasImages: boolean;
    hasClasses: boolean;
    isLocked: boolean;
    pendingCount: number;
    inFlightCount: number;
    lastSavedAt: number | null;
    lastSaveError: string | null;
    onFlushPending: () => void;
    onSetTheme: (t: Theme) => void;
    onBack: () => void;
    onUpload: () => void;
    onLockToggle: () => void;
    onDownloadSnapshot?: () => void;
    /** Trigger a browser-level navigation to the project's bulk image-ZIP
     *  endpoint. Undefined when there are no images yet. */
    onDownloadAllImages?: () => void;
    onOpenAbout: () => void;
    onOpenLicense: () => void;
}

export default function AnnotatorHeader({
    isDarkMode,
    isIgnodeMode,
    projectName,
    imageCount,
    classCount,
    hasImages,
    hasClasses,
    isLocked,
    pendingCount,
    inFlightCount,
    lastSavedAt,
    lastSaveError,
    onFlushPending,
    onSetTheme,
    onBack,
    onUpload,
    onLockToggle,
    onDownloadSnapshot,
    onDownloadAllImages,
    onOpenAbout,
    onOpenLicense,
}: AnnotatorHeaderProps) {
    const { t: tCommon } = useTranslation('common');
    const { t: tNav } = useTranslation('navigation');
    const { t: tCanvas } = useTranslation('canvas');

    // Menu icons get category-tinted colors (Agent Builder pattern) so
    // each row reads as a distinct action at a glance instead of a wall
    // of muted-gray glyphs.
    //
    // Export item is ALWAYS rendered (for discoverability) but disabled
    // until the project is locked — at that point the snapshot exists on
    // S3 and can be streamed back as the YOLO ZIP. The disabled-but-
    // visible state teaches users that lock + export are linked.
    const projectItems: MenuSection['items'] = [];
    if (onDownloadSnapshot) {
        projectItems.push({
            key: 'export-dataset',
            icon: FileArchive,
            iconClassName: isLocked
                ? (isDarkMode ? 'text-emerald-400' : 'text-emerald-600')
                : (isDarkMode ? 'text-gray-500' : 'text-gray-400'),
            label: tCanvas('export_dataset', { defaultValue: 'Export dataset (YOLO ZIP)' }),
            onClick: onDownloadSnapshot,
            disabled: !isLocked,
            title: isLocked
                ? undefined
                : tCanvas('export_disabled_hint', { defaultValue: 'Lock the project to enable export' }),
        });
    }
    if (onDownloadAllImages && hasImages) {
        projectItems.push({
            key: 'download-all-images',
            icon: Archive,
            iconClassName: isDarkMode ? 'text-primary-400' : 'text-primary-600',
            label: tCanvas('download_all_images', { defaultValue: 'Download all images (ZIP)' }),
            onClick: onDownloadAllImages,
        });
    }
    const menuSections: MenuSection[] = [
        ...(projectItems.length > 0 ? [{
            label: tCanvas('project_actions', { defaultValue: 'Project' }),
            items: projectItems,
        }] : []),
        {
            label: tCommon('appearance'),
            items: [
                // Single toggle row (Agent Builder pattern) — icon = the mode
                // you'll switch TO, label says where you're going.
                {
                    key: 'theme-toggle',
                    icon: isDarkMode ? Sun : Moon,
                    iconClassName: isDarkMode ? 'text-amber-400' : 'text-indigo-500',
                    label: isDarkMode
                        ? tCommon('switch_to_light_mode', { defaultValue: 'Switch to Light Mode' })
                        : tCommon('switch_to_dark_mode', { defaultValue: 'Switch to Dark Mode' }),
                    onClick: () => onSetTheme(isDarkMode ? 'light' : 'dark'),
                },
            ],
        },
        {
            label: tCommon('legal'),
            items: [
                {
                    key: 'license',
                    icon: Scale,
                    iconClassName: isDarkMode ? 'text-emerald-400' : 'text-emerald-600',
                    label: tCommon('license'),
                    onClick: onOpenLicense,
                },
                {
                    key: 'about',
                    icon: Info,
                    iconClassName: isDarkMode ? 'text-primary-400' : 'text-primary-600',
                    label: tCommon('about_ignode'),
                    onClick: onOpenAbout,
                },
            ],
        },
    ];

    return (
        <header
            className={cn(
                'h-16 shrink-0 flex items-center justify-between border-b px-3 sm:px-6 z-30 gap-2',
                isDarkMode ? 'bg-gray-900/95 border-gray-700' : 'bg-white/95 border-gray-200',
            )}
        >
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                <IconButton
                    icon={<ArrowLeft className="h-4 w-4" />}
                    label={isIgnodeMode ? tNav('back_to_ignode') : tNav('back')}
                    onClick={onBack}
                    isDarkMode={isDarkMode}
                />
                {/* Brand logo hides on narrow screens — every pixel matters
                    for the project name + action row. */}
                <div className="hidden sm:block">
                    <BrandLogo size="md" />
                </div>
                <div className="min-w-0">
                    <h1 className={cn('text-base sm:text-xl font-bold truncate', isDarkMode ? 'text-gray-100' : 'text-gray-900')}>
                        {projectName ?? tCommon('loading')}
                    </h1>
                    {/* Subtitle collapses on narrow screens — sidebar's
                        filter chip already shows the count. */}
                    <p className={cn('hidden sm:block text-xs truncate', isDarkMode ? 'text-gray-400' : 'text-gray-500')}>
                        {tCanvas('navbar_subtitle', {
                            defaultValue: '{{imageCount}} images · {{classCount}} classes',
                            imageCount,
                            classCount,
                        })}
                    </p>
                </div>
            </div>

            <div className="flex items-center gap-1 sm:gap-2 shrink-0">
                <SaveStateIndicator
                    isDarkMode={isDarkMode}
                    pendingCount={pendingCount}
                    inFlightCount={inFlightCount}
                    lastSavedAt={lastSavedAt}
                    lastSaveError={lastSaveError}
                    onFlush={onFlushPending}
                />
                {/* Below md (768px) buttons collapse to icon-only: text label
                    hides via `hidden md:inline` inside the Button's children
                    span. Icon stays via the leftIcon prop, so the affordance
                    remains identifiable and the touch target stays ~36px. */}
                <Button
                    variant="ghost"
                    isDarkMode={isDarkMode}
                    leftIcon={<ImageIcon className="h-4 w-4" />}
                    onClick={onUpload}
                    disabled={isLocked}
                    title={isLocked ? tCanvas('locked_tooltip') : tCanvas('upload_images', { defaultValue: 'Upload images' })}
                >
                    <span className="hidden md:inline">{tCanvas('upload_images', { defaultValue: 'Upload images' })}</span>
                </Button>
                {isLocked ? (
                    <>
                        {onDownloadSnapshot && (
                            <Button
                                variant="ghost"
                                isDarkMode={isDarkMode}
                                leftIcon={<Download className="h-4 w-4" />}
                                onClick={onDownloadSnapshot}
                                title={tCanvas('download_snapshot', { defaultValue: 'Download snapshot' })}
                            >
                                <span className="hidden md:inline">{tCanvas('download_snapshot', { defaultValue: 'Download snapshot' })}</span>
                            </Button>
                        )}
                        <Button
                            variant="destructive"
                            leftIcon={<LockOpen className="h-4 w-4" />}
                            onClick={onLockToggle}
                            title={tCanvas('unlock_button')}
                        >
                            <span className="hidden md:inline">{tCanvas('unlock_button')}</span>
                        </Button>
                    </>
                ) : (
                    <Button
                        variant="primary"
                        leftIcon={<Lock className="h-4 w-4" />}
                        disabled={!hasImages || !hasClasses}
                        onClick={onLockToggle}
                        title={!hasImages
                            ? tCanvas('snapshot_needs_images', { defaultValue: 'Add images first' })
                            : !hasClasses
                                ? tCanvas('snapshot_needs_classes', { defaultValue: 'Add classes first' })
                                : tCanvas('lock_button')}
                    >
                        <span className="hidden md:inline">{tCanvas('lock_button')}</span>
                    </Button>
                )}
                {/* Locale picker hides on narrow screens — language can be
                    changed from the menu dropdown if needed in v1.1. */}
                <div className="hidden sm:block">
                    <LocalePicker isDarkMode={isDarkMode} />
                </div>
                <MenuDropdown isDarkMode={isDarkMode} sections={menuSections} triggerLabel={tCommon('menu')} />
            </div>
        </header>
    );
}
